package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.mapper.SensitiveDataMapper
import com.evoapps.evoshift.data.mapper.StaffMapper
import com.evoapps.evoshift.data.remote.AppFirestore
import com.evoapps.evoshift.domain.market.payroll.PayrollFact
import com.evoapps.evoshift.domain.repository.EmployeeTimelineRepository
import com.evoapps.evoshift.domain.repository.TimelineDoc
import com.evoapps.evoshift.domain.repository.TimelineWriteBatch
import com.evoapps.evoshift.domain.timeline.TimelineAuditAction
import com.evoapps.evoshift.domain.timeline.TimelineAuditEntry
import com.evoapps.evoshift.domain.timeline.TimelineKind
import com.evoapps.evoshift.domain.timeline.TimelineNote
import com.evoapps.evoshift.domain.util.AppClock
import kotlinx.datetime.LocalDate
import kotlinx.serialization.Serializable

/** SH-212: one note of users/{uid}/timeline/{wpId}. */
@Serializable
internal data class TimelineNoteDto(val id: String = "", val on: String = "", val text: String = "")

/**
 * users/{uid}/timeline/{wpId} (SH-212): the notes and the day after a planned end of cooperation
 * (functions/timeline.js ends the engagement then). firestore.rules (validTimeline) accept exactly
 * these fields, stamped by the writer.
 */
@Serializable
internal data class TimelineDocDto(
    val userId: String = "",
    val workplaceId: String = "",
    val notes: List<TimelineNoteDto> = emptyList(),
    val plannedEndOn: String? = null,
    val updatedAt: String? = null,
    val updatedBy: String? = null
)

/** SH-212: one change of the audit trail (TimelineAuditEntry). */
@Serializable
internal data class TimelineAuditEntryDto(
    val at: String = "",
    val by: String = "",
    val byName: String = "",
    val action: String = "",
    val kind: String = "",
    val fact: String? = null,
    val from: String? = null,
    val until: String? = null,
    val previousFrom: String? = null,
    val previousUntil: String? = null
)

/** users/{uid}/timeline_audit/{wpId} (SH-212): the trail, oldest first; owners and financial managers only. */
@Serializable
internal data class TimelineAuditDto(
    val userId: String = "",
    val workplaceId: String = "",
    val entries: List<TimelineAuditEntryDto> = emptyList(),
    val updatedAt: String? = null,
    val updatedBy: String? = null
)

private fun isDay(value: String): Boolean = runCatching { LocalDate.parse(value) }.isSuccess

internal fun TimelineDocDto.toDomain() = TimelineDoc(
    notes = notes.filter { it.id.isNotEmpty() && isDay(it.on) }.map { TimelineNote(it.id, LocalDate.parse(it.on), it.text) },
    plannedEndOn = plannedEndOn?.takeIf(::isDay)
)

internal fun TimelineAuditEntryDto.toDomain(): TimelineAuditEntry? {
    val action = TimelineAuditAction.entries.firstOrNull { it.name == action } ?: return null
    val kind = TimelineKind.entries.firstOrNull { it.name == kind } ?: return null
    return TimelineAuditEntry(
        at = at, by = by, byName = byName, action = action, kind = kind,
        fact = PayrollFact.entries.firstOrNull { it.name == fact },
        from = from, until = until, previousFrom = previousFrom, previousUntil = previousUntil
    )
}

internal fun TimelineAuditEntry.toDto() = TimelineAuditEntryDto(
    at = at, by = by, byName = byName, action = action.name, kind = kind.name, fact = fact?.name,
    from = from, until = until, previousFrom = previousFrom, previousUntil = previousUntil
)

/**
 * SH-212: the timeline's documents in Firestore, and the batch writing an edit back to the
 * engagement (users/{uid}), the pay terms (users/{uid}/sensitive/{wpId}), the HR dates
 * (users/{uid}/hr/{wpId} with workplaces/{wpId}/readiness/{uid}), the notes and the audit trail;
 * SH-232: the workplace's periods without pay and workplaces/{wpId}/labour_inclusion/{uid}.
 */
class EmployeeTimelineRepositoryImpl(
    private val firestore: AppFirestore,
    private val sensitiveDataMapper: SensitiveDataMapper
) : EmployeeTimelineRepository {

    private fun user(userId: String) = firestore.collection("users").document(userId)
    private fun timeline(userId: String, workplaceId: String) = user(userId).collection("timeline").document(workplaceId)
    private fun audit(userId: String, workplaceId: String) = user(userId).collection("timeline_audit").document(workplaceId)

    override suspend fun getTimelineDoc(userId: String, workplaceId: String): Result<TimelineDoc> = runCatching {
        val snapshot = timeline(userId, workplaceId).get()
        if (!snapshot.exists) TimelineDoc() else snapshot.data<TimelineDocDto>().toDomain()
    }

    override suspend fun getAudit(userId: String, workplaceId: String): Result<List<TimelineAuditEntry>> = runCatching {
        val snapshot = audit(userId, workplaceId).get()
        if (!snapshot.exists) emptyList() else snapshot.data<TimelineAuditDto>().entries.mapNotNull { it.toDomain() }
    }

    override suspend fun save(write: TimelineWriteBatch): Result<Unit> = runCatching {
        val batch = firestore.batch()
        writeInto(batch, write, AppClock.now().toString())
        batch.commit()
    }

    /**
     * SH-269: a bulk change's edits at one workplace together: one batch per [PEOPLE_PER_BATCH]
     * people (each edit is at most a handful of documents; Firestore takes 500 writes a batch).
     */
    override suspend fun saveAll(writes: List<TimelineWriteBatch>): Result<Unit> = runCatching {
        val now = AppClock.now().toString()
        writes.chunked(PEOPLE_PER_BATCH).forEach { chunk ->
            val batch = firestore.batch()
            chunk.forEach { writeInto(batch, it, now) }
            batch.commit()
        }
    }

    private fun writeInto(batch: dev.gitlive.firebase.firestore.WriteBatch, write: TimelineWriteBatch, now: String) {
        val wp = write.workplaceId
        write.engagement?.let { dates ->
            // Field by field: the person's other engagements and this one's role and permission stay.
            batch.updateFields(user(write.userId)) {
                "engagements.$wp.startDate" to dates.startDate
                "engagements.$wp.endDate" to dates.endDate
                "engagements.$wp.isActive" to dates.isActive
            }
        }
        write.financial?.let { batch.set(user(write.userId).collection("sensitive").document(wp), sensitiveDataMapper.toWorkplaceDto(it)) }
        write.staff?.let { record ->
            batch.set(user(write.userId).collection("hr").document(wp), StaffMapper.toDto(record))
            write.readiness?.let { batch.set(firestore.collection("workplaces").document(wp).collection("readiness").document(write.userId), StaffMapper.toDto(it)) }
        }
        write.timelineDoc?.let { doc ->
            batch.set(
                timeline(write.userId, wp),
                TimelineDocDto(
                    userId = write.userId,
                    workplaceId = wp,
                    notes = doc.notes.map { TimelineNoteDto(it.id, it.on.toString(), it.text) },
                    plannedEndOn = doc.plannedEndOn,
                    updatedAt = now,
                    updatedBy = write.editorId
                )
            )
        }
        // SH-232: working without pay (the workplace's periods and the older list) and the labour inclusion setting.
        write.unpaid?.let { unpaid ->
            val periods = unpaid.periods.mapValues { (_, list) -> list.map { com.evoapps.evoshift.data.dto.workplace.UnpaidPeriodDto(it.from, it.until) } }
            batch.updateFields(firestore.collection("workplaces").document(wp)) {
                "settings.unpaidPeriods" to periods
                "settings.unpaidPeriodsAsOf" to unpaid.asOf
                "settings.payroll.unpaidUserIds" to unpaid.unpaidUserIds
            }
        }
        write.labourInclusion?.let { periods ->
            val ref = firestore.collection("workplaces").document(wp).collection("labour_inclusion").document(write.userId)
            if (periods.isEmpty()) batch.delete(ref)
            else batch.set(
                ref,
                LabourInclusionDto(
                    userId = write.userId,
                    periods = periods.map { LabourInclusionPeriodDto(it.fromMonth, it.counts) },
                    updatedAt = now,
                    updatedBy = write.editorId
                )
            )
        }
        batch.set(
            audit(write.userId, wp),
            TimelineAuditDto(userId = write.userId, workplaceId = wp, entries = write.audit.map { it.toDto() }, updatedAt = now, updatedBy = write.editorId)
        )
    }

    private companion object {
        const val PEOPLE_PER_BATCH = 50
    }
}
